/**
 * Escape a single CSV field (RFC-style).
 */
export function escapeCsvField(val) {
  if (val == null || val === undefined) return '';
  const s = String(val);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function rowFromLog(log, formatTimestamp) {
  const ts = log.timestamp || log.time || log.createdAt || log.date;
  return [
    formatTimestamp(ts),
    log.level ?? '',
    log.service ?? '',
    log.message ?? '',
    log.traceId ?? log.id ?? '',
    log.userId ?? '',
    log.ip ?? ''
  ];
}

const CSV_HEADERS = ['Timestamp', 'Level', 'Service', 'Message', 'Trace ID', 'User ID', 'IP'];

/**
 * @param {object[]} logs
 * @param {(ts: unknown) => string} formatTimestamp
 */
export function logsToCsvContent(logs, formatTimestamp) {
  const rows = [CSV_HEADERS, ...logs.map((log) => rowFromLog(log, formatTimestamp))];
  return rows.map((r) => r.map(escapeCsvField).join(',')).join('\r\n');
}

/**
 * Human-readable plain text (one log per block).
 */
export function logsToPlainTextContent(logs, formatTimestamp) {
  const lines = [];
  lines.push(`Commray log export — ${logs.length} entries`);
  lines.push('');
  logs.forEach((log, i) => {
    const ts = log.timestamp || log.time || log.createdAt || log.date;
    lines.push(`--- ${i + 1} ---`);
    lines.push(`Timestamp: ${formatTimestamp(ts)}`);
    lines.push(`Level: ${log.level ?? ''}`);
    lines.push(`Service: ${log.service ?? ''}`);
    lines.push(`Trace ID: ${log.traceId ?? log.id ?? ''}`);
    lines.push(`User ID: ${log.userId ?? ''}`);
    lines.push(`IP: ${log.ip ?? ''}`);
    lines.push(`Message: ${String(log.message ?? '').replace(/\r?\n/g, '\n         ')}`);
    lines.push('');
  });
  return lines.join('\n');
}

export function downloadTextFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** For `showSaveFilePicker` — Chromium browsers */
export const SAVE_PICKER_TYPES_CSV = [
  { description: 'Comma-separated values (*.csv)', accept: { 'text/csv': ['.csv'] } }
];

export const SAVE_PICKER_TYPES_TXT = [
  { description: 'Plain text (*.txt)', accept: { 'text/plain': ['.txt'] } }
];

/**
 * Opens the system save dialog when supported (Chrome, Edge, Opera), so the user can pick folder and filename.
 * Falls back to an immediate download elsewhere (e.g. Safari, Firefox).
 *
 * @param {string} content
 * @param {string} suggestedName filename with extension
 * @param {string} mimeType
 * @param {Array<{ description: string, accept: Record<string, string[]> }>} fileTypes
 */
export async function saveTextFileWithPicker(content, suggestedName, mimeType, fileTypes) {
  const blob = new Blob([content], { type: mimeType });
  if (typeof window !== 'undefined' && typeof window.showSaveFilePicker === 'function') {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName,
        types: fileTypes,
        excludeAcceptAllOption: false
      });
      const writable = await handle.createWritable();
      await writable.write(blob);
      await writable.close();
      return;
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      console.warn('Save file picker failed, falling back to download', err);
    }
  }
  downloadTextFile(content, suggestedName, mimeType);
}

export function slugifyFilenamePart(name) {
  return String(name || 'export')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9._-]+/g, '')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'export';
}
